import { GridAdapter } from "./adapters/GridAdapter";
import {
  DEFAULT_GRID_LAYOUT_PRESETS,
  GridInstance,
  GridLayout,
  GridElementState,
  createGridInstanceId,
  createGridLayoutId,
  createDefaultResponsiveLayouts,
  normalizeGridLayoutMinWidth,
} from "./models/GridInstance";

const STORAGE_PREFIX = "string-grid:";

/**
 * Centralized state manager for all grid instances across all elements.
 *
 * Responsibilities:
 * - Adapter registry (register / lookup)
 * - Per-element responsive layout CRUD
 * - Per-layout grid instance CRUD
 * - Persistence to/from localStorage
 */
export class GridManager {
  private adapterRegistry: Map<string, GridAdapter> = new Map();
  private elementStates: Map<string, GridElementState> = new Map();

  registerAdapter(adapter: GridAdapter): void {
    this.adapterRegistry.set(adapter.type, adapter);
  }

  getAdapter(type: string): GridAdapter | undefined {
    return this.adapterRegistry.get(type);
  }

  getAdapterRegistry(): Map<string, GridAdapter> {
    return this.adapterRegistry;
  }

  getLayouts(elementId: string): GridLayout[] {
    return this.getOrInitState(elementId).layouts;
  }

  getSelectedLayoutId(elementId: string): string {
    return this.getOrInitState(elementId).selectedLayoutId;
  }

  getSelectedLayoutName(elementId: string): string {
    const state = this.getOrInitState(elementId);
    return this.findSelectedLayout(state)?.name ?? "Layout 1";
  }

  getResolvedLayoutId(elementId: string, viewportWidth: number): string {
    const state = this.getOrInitState(elementId);
    return this.findResolvedLayout(state, viewportWidth)?.id ?? state.layouts[0].id;
  }

  getResolvedLayoutName(elementId: string, viewportWidth: number): string {
    const state = this.getOrInitState(elementId);
    return this.findResolvedLayout(state, viewportWidth)?.name ?? state.layouts[0].name;
  }

  getSelectedInstances(elementId: string): GridInstance[] {
    const state = this.getOrInitState(elementId);
    return this.findSelectedLayout(state)?.instances ?? [];
  }

  getResolvedInstances(elementId: string, viewportWidth: number): GridInstance[] {
    const state = this.getOrInitState(elementId);
    return this.findResolvedLayout(state, viewportWidth)?.instances ?? [];
  }

  addLayout(elementId: string): GridLayout {
    const state = this.getOrInitState(elementId);
    const layout = this.findSelectedLayout(state) ?? state.layouts[0];
    this.save(elementId);
    return layout;
  }

  renameLayout(elementId: string, layoutId: string, name: string): void {
    const layout = this.getOrInitState(elementId).layouts.find((entry) => entry.id === layoutId);
    if (!layout) {
      return;
    }

    layout.name = name.trim() || layout.name;
    this.save(elementId);
  }

  selectLayout(elementId: string, layoutId: string, persist: boolean = true): void {
    const state = this.getOrInitState(elementId);
    if (!state.layouts.some((layout) => layout.id === layoutId)) {
      return;
    }

    state.selectedLayoutId = layoutId;
    if (persist) {
      this.save(elementId);
    }
  }

  updateLayoutMinWidth(
    elementId: string,
    layoutId: string,
    minWidth: number,
    persist: boolean = true,
  ): void {
    const state = this.getOrInitState(elementId);
    const layout = state.layouts.find((entry) => entry.id === layoutId);
    if (!layout) {
      return;
    }

    layout.minWidth = normalizeGridLayoutMinWidth(minWidth);
    this.normalizeFixedLayouts(state, layoutId);
    if (persist) {
      this.save(elementId);
    }
  }

  stepLayoutMinWidth(
    elementId: string,
    layoutId: string,
    delta: number,
    persist: boolean = true,
  ): void {
    const layout = this.getOrInitState(elementId).layouts.find((entry) => entry.id === layoutId);
    if (!layout) {
      return;
    }

    this.updateLayoutMinWidth(elementId, layoutId, layout.minWidth + delta, persist);
  }

  replaceLayouts(
    elementId: string,
    layouts: GridLayout[],
    selectedLayoutId: string,
  ): void {
    const state: GridElementState = {
      elementId,
      layouts: this.normalizeLayouts(layouts),
      selectedLayoutId,
    };
    this.ensureValidSelection(state);
    this.elementStates.set(elementId, state);
    this.save(elementId);
  }

  getInstances(elementId: string): GridInstance[] {
    return this.getSelectedInstances(elementId);
  }

  addInstance(elementId: string, type: string): GridInstance | null {
    const adapter = this.adapterRegistry.get(type);
    if (!adapter) {
      return null;
    }

    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) {
      return null;
    }

    const instance: GridInstance = {
      id: createGridInstanceId(),
      type,
      visible: true,
      settings: { ...adapter.getDefaults() },
    };

    layout.instances.push(instance);
    this.save(elementId);
    return instance;
  }

  removeInstance(elementId: string, instanceId: string): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) {
      return;
    }

    layout.instances = layout.instances.filter((instance) => instance.id !== instanceId);
    this.save(elementId);
  }

  reorderInstance(
    elementId: string,
    draggedId: string,
    targetId: string,
    placement: "before" | "after",
  ): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout || draggedId === targetId) {
      return;
    }

    const fromIndex = layout.instances.findIndex((instance) => instance.id === draggedId);
    const targetIndex = layout.instances.findIndex((instance) => instance.id === targetId);
    if (fromIndex === -1 || targetIndex === -1) {
      return;
    }

    const [dragged] = layout.instances.splice(fromIndex, 1);
    const normalizedTargetIndex = layout.instances.findIndex((instance) => instance.id === targetId);
    const insertIndex = placement === "before" ? normalizedTargetIndex : normalizedTargetIndex + 1;

    layout.instances.splice(insertIndex, 0, dragged);
    this.save(elementId);
  }

  moveInstanceToEnd(elementId: string, instanceId: string): void {
    const layout = this.findSelectedLayout(this.getOrInitState(elementId));
    if (!layout) {
      return;
    }

    const fromIndex = layout.instances.findIndex((instance) => instance.id === instanceId);
    if (fromIndex === -1 || fromIndex === layout.instances.length - 1) {
      return;
    }

    const [instance] = layout.instances.splice(fromIndex, 1);
    layout.instances.push(instance);
    this.save(elementId);
  }

  toggleVisibility(elementId: string, instanceId: string): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) {
      return;
    }

    instance.visible = !instance.visible;
    this.save(elementId);
  }

  renameInstance(elementId: string, instanceId: string, name: string): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) {
      return;
    }

    const trimmed = name.trim();
    instance.name = trimmed || undefined;
    this.save(elementId);
  }

  updateSetting(elementId: string, instanceId: string, key: string, value: any): void {
    const instance = this.findInstance(elementId, instanceId);
    if (!instance) {
      return;
    }

    instance.settings[key] = value;
    this.save(elementId);
  }

  findInstance(elementId: string, instanceId: string): GridInstance | undefined {
    return this.getSelectedInstances(elementId).find((instance) => instance.id === instanceId);
  }

  save(elementId: string): void {
    const state = this.elementStates.get(elementId);
    if (!state) {
      return;
    }

    try {
      localStorage.setItem(
        STORAGE_PREFIX + elementId,
        JSON.stringify({
          layouts: state.layouts,
          selectedLayoutId: state.selectedLayoutId,
        }),
      );
    } catch {}
  }

  load(elementId: string): void {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + elementId);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        const valid = parsed.filter((instance) => this.adapterRegistry.has(instance.type));
        for (const instance of valid) {
          const adapter = this.adapterRegistry.get(instance.type);
          if (adapter) {
            instance.settings = { ...adapter.getDefaults(), ...instance.settings };
          }
        }

        const layouts = createDefaultResponsiveLayouts();
        layouts[0].instances = valid;
        this.elementStates.set(elementId, {
          elementId,
          layouts,
          selectedLayoutId: layouts[0].id,
        });
        return;
      }

      const rawLayouts = Array.isArray(parsed?.layouts) ? parsed.layouts : null;
      if (!rawLayouts) {
        return;
      }

      const layouts = this.normalizeLayouts(rawLayouts);
      if (layouts.length === 0) {
        return;
      }

      const selectedLayoutId =
        typeof parsed?.selectedLayoutId === "string"
          ? parsed.selectedLayoutId
          : typeof parsed?.activeLayoutId === "string"
            ? parsed.activeLayoutId
            : layouts[0].id;

      const state: GridElementState = {
        elementId,
        layouts,
        selectedLayoutId,
      };
      this.ensureValidSelection(state);
      this.elementStates.set(elementId, state);
    } catch {}
  }

  clear(): void {
    this.elementStates.clear();
  }

  private getOrInitState(elementId: string): GridElementState {
    let state = this.elementStates.get(elementId);
    if (!state) {
      const layouts = createDefaultResponsiveLayouts();
      state = {
        elementId,
        layouts,
        selectedLayoutId: layouts[0].id,
      };
      this.elementStates.set(elementId, state);
    }
    this.sortLayouts(state);
    this.ensureValidSelection(state);
    return state;
  }

  private normalizeLayouts(layouts: any[]): GridLayout[] {
    const normalized = layouts
      .filter(
        (layout: any) =>
          layout &&
          typeof layout === "object" &&
          typeof layout.id === "string" &&
          typeof layout.name === "string",
      )
      .map((layout: any, index: number) => {
        const instances = Array.isArray(layout.instances)
          ? layout.instances
          : Array.isArray(layout.grids)
            ? layout.grids
            : [];

        return {
          id: layout.id,
          name: layout.name,
          minWidth: this.resolveLoadedMinWidth(layout.minWidth, index),
          instances: instances
            .filter((instance: any) => this.adapterRegistry.has(instance.type))
            .map((instance: any) => {
              const adapter = this.adapterRegistry.get(instance.type);
              return adapter
                ? {
                    ...instance,
                    visible: instance.visible !== false,
                    settings: { ...adapter.getDefaults(), ...instance.settings },
                  }
                : instance;
            }),
        } satisfies GridLayout;
      });

    return this.createFixedLayouts(normalized);
  }

  private resolveLoadedMinWidth(value: unknown, index: number): number {
    if (typeof value === "number" && Number.isFinite(value)) {
      return normalizeGridLayoutMinWidth(value);
    }

    const preset = DEFAULT_GRID_LAYOUT_PRESETS[index];
    if (preset) {
      return preset.minWidth;
    }

    const phoneBase = DEFAULT_GRID_LAYOUT_PRESETS[DEFAULT_GRID_LAYOUT_PRESETS.length - 1].minWidth;
    return Math.max(
      0,
      phoneBase - (index - (DEFAULT_GRID_LAYOUT_PRESETS.length - 1)) * DEFAULT_LAYOUT_STEP,
    );
  }

  private sortLayouts(state: GridElementState): void {
    state.layouts.sort((left, right) => right.minWidth - left.minWidth);
  }

  private ensureValidSelection(state: GridElementState): void {
    if (state.layouts.length === 0) {
      state.layouts = createDefaultResponsiveLayouts();
    }

    if (state.layouts.length !== DEFAULT_GRID_LAYOUT_PRESETS.length) {
      state.layouts = this.createFixedLayouts(state.layouts);
    }

    if (!state.layouts.some((layout) => layout.id === state.selectedLayoutId)) {
      state.selectedLayoutId = state.layouts[0].id;
    }
  }

  private findSelectedLayout(state: GridElementState): GridLayout | undefined {
    return state.layouts.find((layout) => layout.id === state.selectedLayoutId);
  }

  private findResolvedLayout(state: GridElementState, viewportWidth: number): GridLayout | undefined {
    const sorted = [...state.layouts].sort((left, right) => right.minWidth - left.minWidth);
    for (const layout of sorted) {
      if (viewportWidth >= layout.minWidth) {
        return layout;
      }
    }
    return sorted[sorted.length - 1];
  }

  private createFixedLayouts(layouts: GridLayout[]): GridLayout[] {
    const sorted = [...layouts].sort((left, right) => right.minWidth - left.minWidth);
    const fallback = createDefaultResponsiveLayouts();

    const fixed = DEFAULT_GRID_LAYOUT_PRESETS.map((preset, index) => {
      const source = sorted[index];
      return {
        id: source?.id ?? fallback[index].id,
        name: preset.name,
        minWidth:
          index === DEFAULT_GRID_LAYOUT_PRESETS.length - 1
            ? 0
            : source
              ? normalizeGridLayoutMinWidth(source.minWidth)
              : preset.minWidth,
        instances: source ? [...source.instances] : [],
      } satisfies GridLayout;
    });

    const state: GridElementState = {
      elementId: "",
      layouts: fixed,
      selectedLayoutId: fixed[0].id,
    };
    this.normalizeFixedLayouts(state);
    return state.layouts;
  }

  private normalizeFixedLayouts(state: GridElementState, preferredSelectedId?: string): void {
    this.sortLayouts(state);

    const [large, medium, small] = state.layouts;
    if (!large || !medium || !small) {
      return;
    }

    large.name = DEFAULT_GRID_LAYOUT_PRESETS[0].name;
    medium.name = DEFAULT_GRID_LAYOUT_PRESETS[1].name;
    small.name = DEFAULT_GRID_LAYOUT_PRESETS[2].name;

    large.minWidth = Math.max(medium.minWidth + 1, normalizeGridLayoutMinWidth(large.minWidth));
    medium.minWidth = Math.max(1, normalizeGridLayoutMinWidth(medium.minWidth));
    if (medium.minWidth >= large.minWidth) {
      medium.minWidth = Math.max(1, large.minWidth - 1);
    }
    small.minWidth = 0;

    if (preferredSelectedId && state.layouts.some((layout) => layout.id === preferredSelectedId)) {
      state.selectedLayoutId = preferredSelectedId;
    }
  }
}
